import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  isAndroid,
  isIOS,
  isMobileDevice,
  shouldUseMediaRecorderFallback,
  supportsMediaRecorderCapture,
  supportsWebSpeechRecognition,
  markWebSpeechUnreliable,
  clearWebSpeechUnreliable,
} from '../utils/voiceCaptureSupport';
import {
  warmUpMicrophone,
  transcribeAudioBlob,
  createMediaRecorder,
  isTranscriptionUnavailableError,
  resolveVoiceLanguageCode,
} from '../utils/mobileAudioCapture';

// Speech Recognition API types
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
}

interface SpeechRecognitionEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message: string;
}

interface GlobalVoiceInputHandlerProps {
  onTranscription: (transcript: string) => void;
  onProcessingChange: (isProcessing: boolean) => void;
}

// Map an app language code (e.g. "en", "ar") to a Web Speech locale.
const SPEECH_LOCALES: Record<string, string> = {
  en: 'en-US', ar: 'ar-SA', hi: 'hi-IN', ur: 'ur-PK', bn: 'bn-BD',
  fr: 'fr-FR', de: 'de-DE', ja: 'ja-JP', zh: 'zh-CN'
};

// How long to wait before speech starts (user opens mic and thinks).
const INITIAL_SILENCE_TIMEOUT_MS = 12000;
// How long to wait after the user pauses mid-sentence.
const SPEECH_SILENCE_TIMEOUT_MS = 5500;
// Hard cap so recording cannot run indefinitely.
const MAX_RECORDING_MS = 90000;
// Volume threshold for detecting speech during MediaRecorder capture.
const SPEECH_VOLUME_THRESHOLD = 0.08;
const MOBILE_SPEECH_VOLUME_THRESHOLD = 0.055;
// Hysteresis band: ambient noise often sits between silence and speech levels.
const SILENCE_VOLUME_RATIO = 0.55;
// While the user is speaking, extend the pause timer at most this often.
const SPEECH_PAUSE_RESET_MS = 3000;

const getSpeechVolumeThreshold = (): number => (
  isMobileDevice() ? MOBILE_SPEECH_VOLUME_THRESHOLD : SPEECH_VOLUME_THRESHOLD
);

const getSilenceVolumeThreshold = (): number => (
  getSpeechVolumeThreshold() * SILENCE_VOLUME_RATIO
);

const getMediaRecorderTimesliceMs = (): number => (
  isAndroid() || isIOS() ? 1000 : 250
);

// Multiple handler instances can be mounted at once (Layout always mounts one;
// the /voice-assistant page mounts its own on iOS). Only the most recently
// mounted instance may react to start/stop events, otherwise a single mic
// press starts two parallel recordings.
const handlerStack: symbol[] = [];

const resolveSpeechLocale = (): string => {
  try {
    const raw = localStorage.getItem('voiceSettings');
    if (raw) {
      const parsed = JSON.parse(raw);
      const lang = parsed?.default_language;
      if (lang && SPEECH_LOCALES[lang]) return SPEECH_LOCALES[lang];
    }
  } catch {
    /* ignore malformed cache */
  }
  const i18nLang = (localStorage.getItem('i18nextLng') || '').slice(0, 2);
  return SPEECH_LOCALES[i18nLang] || 'en-US';
};

const GlobalVoiceInputHandler: React.FC<GlobalVoiceInputHandlerProps> = ({
  onTranscription,
  onProcessingChange
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef<string>('');
  const isListeningRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const usingMediaRecorderRef = useRef(false);
  const mediaRecorderFallbackAttemptedRef = useRef(false);
  const startInProgressRef = useRef(false);
  const previewTranscribingRef = useRef(false);
  const previewTranscriptRef = useRef('');
  const previewIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Web Audio analysis for the live waveform
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);

  // Silence detection state
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxRecordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasSpeechRef = useRef<boolean>(false);
  const recordingStartedAtRef = useRef<number>(0);
  const wasAboveSpeechThresholdRef = useRef(false);
  const lastVolumeResetAtRef = useRef(0);

  const onTranscriptionRef = useRef(onTranscription);
  const onProcessingChangeRef = useRef(onProcessingChange);
  const createRecognitionSessionRef = useRef<() => SpeechRecognition | null>(() => null);

  useEffect(() => {
    onTranscriptionRef.current = onTranscription;
    onProcessingChangeRef.current = onProcessingChange;
  }, [onTranscription, onProcessingChange]);

  const clearSilenceTimeout = useCallback(() => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  }, []);

  const clearRecordingTimers = useCallback(() => {
    clearSilenceTimeout();
    if (maxRecordingTimeoutRef.current) {
      clearTimeout(maxRecordingTimeoutRef.current);
      maxRecordingTimeoutRef.current = null;
    }
  }, [clearSilenceTimeout]);

  const clearPreviewTranscription = useCallback(() => {
    if (previewIntervalRef.current) {
      clearInterval(previewIntervalRef.current);
      previewIntervalRef.current = null;
    }
    previewTranscribingRef.current = false;
    previewTranscriptRef.current = '';
  }, []);

  const getSilenceTimeoutMs = useCallback(() => (
    hasSpeechRef.current ? SPEECH_SILENCE_TIMEOUT_MS : INITIAL_SILENCE_TIMEOUT_MS
  ), []);

  const releaseMicrophoneStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const ensureMicrophoneStream = useCallback(async (): Promise<MediaStream | null> => {
    const stream = await warmUpMicrophone(mediaStreamRef.current);
    if (stream) {
      mediaStreamRef.current = stream;
    }
    return stream;
  }, []);

  const stopWaveform = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    window.dispatchEvent(new CustomEvent('voiceWaveform', { detail: { levels: [], volume: 0 } }));
  }, []);

  const startWaveform = useCallback(async (existingStream?: MediaStream | null) => {
    try {
      if (audioContextRef.current) return;

      const stream = existingStream || mediaStreamRef.current || await ensureMicrophoneStream();
      if (!stream) return;

      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const audioContext: AudioContext = new AudioCtx();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const BAR_COUNT = 28;

      const tick = () => {
        if (!analyserRef.current) return;

        if (audioContextRef.current?.state === 'suspended') {
          void audioContextRef.current.resume().catch(() => {});
        }

        analyserRef.current.getByteFrequencyData(dataArray);

        const levels: number[] = new Array(BAR_COUNT);
        const step = Math.floor(bufferLength / BAR_COUNT) || 1;
        let sum = 0;
        for (let i = 0; i < BAR_COUNT; i++) {
          const v = dataArray[i * step] / 255;
          levels[i] = v;
          sum += v;
        }
        const volume = sum / BAR_COUNT;

        if (usingMediaRecorderRef.current && isListeningRef.current) {
          const speechThreshold = getSpeechVolumeThreshold();
          const silenceThreshold = getSilenceVolumeThreshold();

          if (volume > speechThreshold) {
            if (!hasSpeechRef.current) {
              hasSpeechRef.current = true;
            }
            const now = Date.now();
            const risingEdge = !wasAboveSpeechThresholdRef.current;
            const sustainedSpeech = now - lastVolumeResetAtRef.current >= SPEECH_PAUSE_RESET_MS;
            if (risingEdge || sustainedSpeech) {
              lastVolumeResetAtRef.current = now;
              resetSilenceDetectionRef.current();
            }
            wasAboveSpeechThresholdRef.current = true;
          } else if (volume < silenceThreshold) {
            wasAboveSpeechThresholdRef.current = false;
          }
        }

        window.dispatchEvent(new CustomEvent('voiceWaveform', { detail: { levels, volume } }));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      console.warn('Waveform analysis unavailable:', err);
    }
  }, [ensureMicrophoneStream]);

  const stopRecognition = useCallback(() => {
    startInProgressRef.current = false;
    stopRequestedRef.current = true;
    clearRecordingTimers();
    clearPreviewTranscription();

    if (usingMediaRecorderRef.current && mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        try {
          if (typeof mediaRecorderRef.current.requestData === 'function') {
            mediaRecorderRef.current.requestData();
          }
          mediaRecorderRef.current.stop();
        } catch {
          /* already stopped */
        }
      }
      return;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        /* already stopped */
      }
    }
  }, [clearPreviewTranscription, clearRecordingTimers]);

  const scheduleMaxRecordingTimeout = useCallback(() => {
    if (maxRecordingTimeoutRef.current || !recordingStartedAtRef.current) return;
    const elapsed = Date.now() - recordingStartedAtRef.current;
    const remaining = Math.max(MAX_RECORDING_MS - elapsed, 1000);
    maxRecordingTimeoutRef.current = setTimeout(() => {
      if (!isListeningRef.current) return;
      console.log('⏰ Max recording duration reached, stopping recording');
      stopRecognition();
    }, remaining);
  }, [stopRecognition]);

  const startSilenceDetection = useCallback(() => {
    clearSilenceTimeout();

    silenceTimeoutRef.current = setTimeout(() => {
      if (!isListeningRef.current) return;
      console.log(
        hasSpeechRef.current
          ? '🔇 Pause detected after speech, stopping recording'
          : '🔇 No speech detected within listening window, stopping recording'
      );
      window.dispatchEvent(new CustomEvent('silenceDetected'));
      stopRecognition();
    }, getSilenceTimeoutMs());
  }, [clearSilenceTimeout, getSilenceTimeoutMs, stopRecognition]);

  const resetSilenceDetectionRef = useRef<() => void>(() => {});

  const resetSilenceDetection = useCallback(() => {
    if (!isListeningRef.current) return;
    startSilenceDetection();
  }, [startSilenceDetection]);

  useEffect(() => {
    resetSilenceDetectionRef.current = resetSilenceDetection;
  }, [resetSilenceDetection]);

  const finishMediaRecorderCapture = useCallback(async () => {
    startInProgressRef.current = false;
    usingMediaRecorderRef.current = false;
    isListeningRef.current = false;
    recordingStartedAtRef.current = 0;
    stopWaveform();
    clearRecordingTimers();
    clearPreviewTranscription();
    window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));

    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    releaseMicrophoneStream();

    if (!chunks.length) {
      onProcessingChangeRef.current(false);
      window.dispatchEvent(new CustomEvent('voiceMicError', {
        detail: {
          message: 'No audio was captured. Please check your microphone and try again.',
        },
      }));
      return;
    }

    const mimeType = chunks[0]?.type || 'audio/webm';
    const blob = new Blob(chunks, { type: mimeType });

    if (blob.size < 512) {
      onProcessingChangeRef.current(false);
      window.dispatchEvent(new CustomEvent('voiceMicError', {
        detail: {
          message: 'No audio was captured. Please check your microphone and try again.',
        },
      }));
      return;
    }

    const voiceLanguage = resolveVoiceLanguageCode();

    let webSpeechFallbackStarted = false;

    const tryWebSpeechFallback = (): boolean => {
      // Mobile browsers must stay on MediaRecorder + server transcription.
      // Falling back to Web Speech after a server failure produces endless
      // "Listening…" sessions with no transcript on iOS and Android.
      if (isMobileDevice()) return false;
      if (!supportsWebSpeechRecognition()) return false;
      if (!recognitionRef.current) {
        createRecognitionSessionRef.current();
      }
      if (!recognitionRef.current) return false;
      try {
        hasSpeechRef.current = false;
        finalTranscriptRef.current = '';
        stopRequestedRef.current = false;
        recognitionRef.current.start();
        return true;
      } catch (error) {
        console.warn('Web Speech fallback failed:', error);
        return false;
      }
    };

    try {
      setIsProcessing(true);
      onProcessingChangeRef.current(true);
      const text = await transcribeAudioBlob(blob, voiceLanguage);
      const previewText = previewTranscriptRef.current.trim();
      const finalText = text.length > 1 ? text : previewText;
      if (finalText.length > 1) {
        onTranscriptionRef.current(finalText);
      } else {
        window.dispatchEvent(new CustomEvent('voiceMicError', {
          detail: { message: 'No speech detected in the recording. Please try again.' },
        }));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Transcription failed';
      console.warn('Media recorder transcription failed:', error);

      // If interim preview already captured speech, continue with that text
      // instead of dropping the command on final transcription failure.
      const previewText = previewTranscriptRef.current.trim();
      if (previewText.length > 1) {
        onTranscriptionRef.current(previewText);
        return;
      }

      if (isTranscriptionUnavailableError(message) && tryWebSpeechFallback()) {
        webSpeechFallbackStarted = true;
        return;
      }

      const unavailableMessage = supportsWebSpeechRecognition()
        ? 'Voice transcription is temporarily unavailable. Tap the mic again to use browser speech, or type your message.'
        : 'Voice transcription is temporarily unavailable. Please type your message instead.';
      window.dispatchEvent(new CustomEvent('voiceMicError', {
        detail: {
          message: isTranscriptionUnavailableError(message) ? unavailableMessage : message,
        },
      }));
    } finally {
      if (!webSpeechFallbackStarted) {
        setIsProcessing(false);
        onProcessingChangeRef.current(false);
      }
    }
  }, [clearPreviewTranscription, clearRecordingTimers, releaseMicrophoneStream, stopWaveform]);

  const startMediaRecorderCapture = useCallback(async (stream: MediaStream) => {
    audioChunksRef.current = [];
    usingMediaRecorderRef.current = true;
    stopRequestedRef.current = false;
    recordingStartedAtRef.current = Date.now();
    hasSpeechRef.current = false;
    finalTranscriptRef.current = '';
    previewTranscriptRef.current = '';
    wasAboveSpeechThresholdRef.current = false;
    lastVolumeResetAtRef.current = 0;

    const recorder = createMediaRecorder(stream, (chunk) => {
      audioChunksRef.current.push(chunk);
    });

    recorder.addEventListener('stop', () => {
      void finishMediaRecorderCapture();
    });

    mediaRecorderRef.current = recorder;
    recorder.start(getMediaRecorderTimesliceMs());
    isListeningRef.current = true;
    startInProgressRef.current = false;

    // Near-real-time transcript updates during mobile MediaRecorder capture.
    if (isMobileDevice()) {
      const voiceLanguage = resolveVoiceLanguageCode();
      previewIntervalRef.current = setInterval(async () => {
        if (!isListeningRef.current || previewTranscribingRef.current) return;
        if (audioChunksRef.current.length < 3) return;

        previewTranscribingRef.current = true;
        try {
          const previewBlob = new Blob(audioChunksRef.current, {
            type: audioChunksRef.current[0]?.type || 'audio/webm',
          });
          const previewText = (await transcribeAudioBlob(previewBlob, voiceLanguage)).trim();
          if (previewText.length > 1 && previewText !== previewTranscriptRef.current) {
            const previousLength = previewTranscriptRef.current.length;
            previewTranscriptRef.current = previewText;
            hasSpeechRef.current = true;
            // Only extend the pause window when the transcript meaningfully grows.
            if (previewText.length >= previousLength + 2 || previousLength === 0) {
              resetSilenceDetectionRef.current();
            }
            window.dispatchEvent(new CustomEvent('transcriptUpdate', {
              detail: { transcript: previewText, isFinal: false },
            }));
          }
        } catch {
          // Ignore interim transcription errors/no-speech and keep listening.
        } finally {
          previewTranscribingRef.current = false;
        }
      }, 5000);
    }

    await startWaveform(stream);
    startSilenceDetection();
    scheduleMaxRecordingTimeout();
    window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: true }));
  }, [finishMediaRecorderCapture, scheduleMaxRecordingTimeout, startSilenceDetection, startWaveform]);

  const createRecognitionSession = useCallback(() => {
    if (!supportsWebSpeechRecognition()) return null;

    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {
        try {
          recognitionRef.current.stop();
        } catch {
          /* already stopped */
        }
      }
      recognitionRef.current = null;
    }

    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognitionCtor() as SpeechRecognition;

    recognition.continuous = !isMobileDevice();
    recognition.interimResults = true;
    recognition.lang = resolveSpeechLocale();

    recognition.onstart = () => {
      console.log('🎤 Global voice input started');
      startInProgressRef.current = false;
      isListeningRef.current = true;
      stopRequestedRef.current = false;
      if (!recordingStartedAtRef.current) {
        recordingStartedAtRef.current = Date.now();
      }
      hasSpeechRef.current = false;
      finalTranscriptRef.current = '';
      wasAboveSpeechThresholdRef.current = false;
      lastVolumeResetAtRef.current = 0;

      void startWaveform(mediaStreamRef.current);
      startSilenceDetection();
      scheduleMaxRecordingTimeout();
      window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: true }));
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript.trim() || interimTranscript.trim()) {
        hasSpeechRef.current = true;
        resetSilenceDetection();
      } else if (hasSpeechRef.current) {
        resetSilenceDetection();
      }

      if (finalTranscript) {
        finalTranscriptRef.current = `${finalTranscriptRef.current} ${finalTranscript}`.replace(/\s+/g, ' ').trim();
        console.log('📝 Global voice input final transcript:', finalTranscriptRef.current);
      }

      const currentTranscript = `${finalTranscriptRef.current} ${interimTranscript}`.replace(/\s+/g, ' ').trim();
      if (currentTranscript) {
        window.dispatchEvent(new CustomEvent('transcriptUpdate', {
          detail: {
            transcript: currentTranscript,
            isFinal: finalTranscript.length > 0
          }
        }));
      }
    };

    recognition.onend = () => {
      // We switched to MediaRecorder capture mid-session (Web Speech service
      // error); that capture owns the stream and timers now.
      if (usingMediaRecorderRef.current) return;

      const elapsed = recordingStartedAtRef.current
        ? Date.now() - recordingStartedAtRef.current
        : 0;
      // Android Chrome (continuous = false) often ends the session after a few
      // quiet seconds; silently restart while we are still inside the initial
      // listening window so the mic does not appear to cut out.
      // Mobile Web Speech sessions end quickly (continuous=false). Restarting
      // them while still waiting for speech caused endless "Listening…" loops.
      const shouldRetryForSpeech = !isMobileDevice()
        && !stopRequestedRef.current
        && !hasSpeechRef.current
        && !finalTranscriptRef.current.trim()
        && elapsed > 0
        && elapsed < INITIAL_SILENCE_TIMEOUT_MS;

      if (shouldRetryForSpeech && recognitionRef.current) {
        console.log('🔄 Restarting recognition — still waiting for you to speak');
        try {
          recognitionRef.current.start();
          resetSilenceDetection();
          return;
        } catch (error) {
          console.warn('Could not restart recognition while waiting for speech:', error);
        }
      }

      console.log('🎤 Voice input ended');
      startInProgressRef.current = false;
      isListeningRef.current = false;
      recordingStartedAtRef.current = 0;
      stopWaveform();
      releaseMicrophoneStream();
      clearRecordingTimers();
      clearPreviewTranscription();
      onProcessingChangeRef.current(false);
      window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));

      const currentTranscript = finalTranscriptRef.current.trim();
      finalTranscriptRef.current = '';

      if (currentTranscript && currentTranscript.length > 1) {
        console.log('🎤 Processing voice input:', currentTranscript);
        clearWebSpeechUnreliable();
        setIsProcessing(true);
        onProcessingChangeRef.current(true);
        onTranscriptionRef.current(currentTranscript);
      } else {
        console.log('🎤 No speech detected, ignoring');
        setIsProcessing(false);
        onProcessingChangeRef.current(false);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === 'no-speech' && !hasSpeechRef.current && !stopRequestedRef.current) {
        console.log('🔇 Browser reported no speech yet — keeping the mic open');
        resetSilenceDetection();
        return;
      }

      // Web Speech service failures (e.g. Android devices without Google
      // services, blocked speech servers): switch to MediaRecorder + server
      // Whisper in the same capture instead of dropping the command.
      const isServiceError = ['network', 'service-not-allowed', 'language-not-supported'].includes(event.error);
      if (isServiceError) {
        markWebSpeechUnreliable();
      }
      if (
        isServiceError
        && !stopRequestedRef.current
        && !mediaRecorderFallbackAttemptedRef.current
        && supportsMediaRecorderCapture()
        && mediaStreamRef.current?.active
      ) {
        console.warn(`⚠️ Web Speech failed (${event.error}) — switching to server transcription`);
        mediaRecorderFallbackAttemptedRef.current = true;
        recognitionRef.current = null;
        clearRecordingTimers();
        void startMediaRecorderCapture(mediaStreamRef.current).catch((fallbackError) => {
          console.warn('MediaRecorder fallback after Web Speech error failed:', fallbackError);
        });
        return;
      }

      console.warn('⚠️ Global voice input error:', event.error);
      startInProgressRef.current = false;
      isListeningRef.current = false;
      recordingStartedAtRef.current = 0;
      stopWaveform();
      releaseMicrophoneStream();
      clearRecordingTimers();
      clearPreviewTranscription();
      onProcessingChangeRef.current(false);
      window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));

      const errorMessages: Record<string, string> = {
        'not-allowed': 'Microphone access denied. Please allow microphone access in your browser settings.',
        'audio-capture': 'No microphone was found. Please check that a microphone is connected and try again.',
        network: 'Speech recognition needs a network connection. Please check your connection, or type your message.',
        'service-not-allowed': 'Speech recognition is unavailable on this device. Please type your message instead.',
        'language-not-supported': 'This language is not supported by your browser\u2019s speech recognition. Please type your message.',
      };
      const userMessage = errorMessages[event.error];
      if (userMessage && !stopRequestedRef.current) {
        window.dispatchEvent(new CustomEvent('voiceMicError', { detail: { message: userMessage } }));
      }
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [clearPreviewTranscription, clearRecordingTimers, releaseMicrophoneStream, resetSilenceDetection, scheduleMaxRecordingTimeout, startSilenceDetection, startWaveform, stopWaveform]);

  useEffect(() => {
    createRecognitionSessionRef.current = createRecognitionSession;
  }, [createRecognitionSession]);

  useEffect(() => () => {
    stopRecognition();
    recognitionRef.current = null;
    clearRecordingTimers();
    clearPreviewTranscription();
    stopWaveform();
    releaseMicrophoneStream();
  }, [clearPreviewTranscription, clearRecordingTimers, releaseMicrophoneStream, stopRecognition, stopWaveform]);

  // Listen for global voice input events
  useEffect(() => {
    const instanceId = Symbol('voice-input-handler');
    handlerStack.push(instanceId);
    const isActiveInstance = () => handlerStack[handlerStack.length - 1] === instanceId;

    const handleStartVoiceInput = async (event: Event) => {
      if (!isActiveInstance()) return;
      console.log('🎤 Start voice input event received');
      if (isListeningRef.current || startInProgressRef.current) {
        // A second mic press while recording means "stop" — never queue a
        // restart, which produced an endless stop/start recording loop on
        // mobile when the UI's recording state was momentarily stale.
        if (isListeningRef.current) {
          stopRecognition();
        }
        return;
      }
      startInProgressRef.current = true;

      recordingStartedAtRef.current = 0;
      hasSpeechRef.current = false;
      finalTranscriptRef.current = '';
      wasAboveSpeechThresholdRef.current = false;
      lastVolumeResetAtRef.current = 0;
      stopRequestedRef.current = false;
      mediaRecorderFallbackAttemptedRef.current = false;
      window.dispatchEvent(new CustomEvent('transcriptUpdate', {
        detail: { transcript: '', isFinal: false },
      }));

      const prewarmed = (event as CustomEvent<{ stream?: MediaStream | null }>).detail?.stream;
      const stream = prewarmed?.active ? prewarmed : await ensureMicrophoneStream();
      if (stream) {
        mediaStreamRef.current = stream;
      }
      if (!stream) {
        startInProgressRef.current = false;
        window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
        window.dispatchEvent(new CustomEvent('voiceMicError', {
          detail: { message: 'Microphone access denied. Please allow microphone access in your browser settings.' },
        }));
        return;
      }

      if (shouldUseMediaRecorderFallback()) {
        try {
          await startMediaRecorderCapture(stream);
        } catch (error) {
          console.warn('Could not start media recorder capture:', error);
          startInProgressRef.current = false;
          window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
          releaseMicrophoneStream();
          window.dispatchEvent(new CustomEvent('voiceMicError', {
            detail: {
              message: 'Could not start voice recording. Please check microphone permissions and try again.',
            },
          }));
        }
        return;
      }

      const recognition = createRecognitionSession();
      if (!recognition) {
        startInProgressRef.current = false;
        window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
        window.dispatchEvent(new CustomEvent('voiceMicError', {
          detail: { message: 'Speech recognition is not supported on this device.' },
        }));
        releaseMicrophoneStream();
        return;
      }

      try {
        recognition.start();
      } catch (error) {
        console.warn('Could not start recognition (may already be active):', error);
        const retryRecognition = createRecognitionSession();
        if (!retryRecognition) {
          startInProgressRef.current = false;
          window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
          releaseMicrophoneStream();
          return;
        }

        setTimeout(() => {
          if (!recognitionRef.current || isListeningRef.current || stopRequestedRef.current) {
            startInProgressRef.current = false;
            return;
          }
          try {
            recognitionRef.current.start();
          } catch (retryError) {
            console.warn('Recognition retry failed:', retryError);
            startInProgressRef.current = false;
            window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
            releaseMicrophoneStream();
          }
        }, 120);
      }
    };

    const handleStopVoiceInput = () => {
      console.log('🎤 Stop voice input event received');
      stopRecognition();
    };

    const handleRecordingCleanup = () => {
      if (isListeningRef.current || startInProgressRef.current) {
        console.log('🎤 Voice recording cleanup requested (navigation)');
        stopRecognition();
      }
    };

    window.addEventListener('startVoiceInput', handleStartVoiceInput);
    window.addEventListener('stopVoiceInput', handleStopVoiceInput);
    window.addEventListener('voice-recording-cleanup', handleRecordingCleanup);

    return () => {
      const index = handlerStack.indexOf(instanceId);
      if (index !== -1) handlerStack.splice(index, 1);
      window.removeEventListener('startVoiceInput', handleStartVoiceInput);
      window.removeEventListener('stopVoiceInput', handleStopVoiceInput);
      window.removeEventListener('voice-recording-cleanup', handleRecordingCleanup);
    };
  }, [createRecognitionSession, ensureMicrophoneStream, releaseMicrophoneStream, startMediaRecorderCapture, stopRecognition]);

  return null;
};

export default GlobalVoiceInputHandler;
